import React, { type FormEvent, useCallback, useRef, useContext, useState } from "react";
import { type Room, EventType } from "matrix-js-sdk/src/matrix";

import { _t } from "../../../languageHandler";
import { type ICompletion } from "../../../autocomplete/Autocompleter";
import UserProvider from "../../../autocomplete/UserProvider";
import { AutocompleteInput } from "../../structures/AutocompleteInput";
import PowerSelector from "../elements/PowerSelector";
import MatrixClientContext from "../../../contexts/MatrixClientContext";
import AccessibleButton from "../elements/AccessibleButton";
import Modal from "../../../Modal";
import ErrorDialog from "../dialogs/ErrorDialog";
import SettingsFieldset from "./SettingsFieldset";

interface AddPrivilegedUsersProps {
    room: Room;
    defaultUserLevel: number;
}

export const AddPrivilegedUsers: React.FC<AddPrivilegedUsersProps> = ({ room, defaultUserLevel }) => {
    const client = useContext(MatrixClientContext);
    const userProvider = useRef(new UserProvider(room));
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [powerLevel, setPowerLevel] = useState<number>(defaultUserLevel);
    const [selectedUsers, setSelectedUsers] = useState<ICompletion[]>([]);
    const hasLowerOrEqualLevelThanDefaultLevelFilter = useCallback(
        (user: ICompletion) => compareLevelThanDefaultLevel(
            room,
            user,
            defaultUserLevel,
            NumberComparison.LessThanOrEqual),
        [room, defaultUserLevel],
    );

    const onSubmit = async (event: FormEvent): Promise<void> => {
        event.preventDefault();
        setIsLoading(true);

        const userIds = getUserIdsFromCompletions(selectedUsers);
        const powerLevelEvent = room.currentState.getStateEvents(EventType.RoomPowerLevels, "");

        // `RoomPowerLevels` event should exist, but technically it is not guaranteed.
        if (powerLevelEvent === null) {
            Modal.createDialog(ErrorDialog, {
                title: _t("common.error"),
                description: _t("error.update_power_level"),
            });

            return;
        }

        try {
            await client.setPowerLevel(room.roomId, userIds, powerLevel);
            setSelectedUsers([]);
            setPowerLevel(defaultUserLevel);
        } catch {
            Modal.createDialog(ErrorDialog, {
                title: _t("common.error"),
                description: _t("error.update_power_level"),
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form style={{ display: "flex" }} onSubmit={onSubmit}>
            <SettingsFieldset
                legend={_t("room_settings.permissions.add_privileged_user_heading")}
                description={_t("room_settings.permissions.add_privileged_user_description")}
                style={{ flexGrow: 1 }}
            >
                <AutocompleteInput
                    provider={userProvider.current}
                    placeholder={_t("room_settings.permissions.add_privileged_user_filter_placeholder")}
                    onSelectionChange={setSelectedUsers}
                    selection={selectedUsers}
                    additionalFilter={hasLowerOrEqualLevelThanDefaultLevelFilter}
                />
                <PowerSelector value={powerLevel} minValue={powerLevel} onChange={setPowerLevel} />
                <AccessibleButton
                    type="submit"
                    element="button"
                    kind="primary"
                    disabled={!selectedUsers.length || isLoading}
                    onClick={null}
                    data-testid="add-privileged-users-submit-button"
                >
                    {_t("action.apply")}
                </AccessibleButton>
            </SettingsFieldset>
        </form>
    );
};

/**
 * Enum representing the different types of number comparisons that can be made.
 */
export enum NumberComparison {
    LessThan = "LESS_THAN",
    LessThanOrEqual = "LESS_THAN_OR_EQUAL",
    Equal = "EQUAL",
    GreaterThanOrEqual = "GREATER_THAN_OR_EQUAL",
    GreaterThan = "GREATER_THAN",
    NotEqual = "NOT_EQUAL"
}

/**
 * Compare the power level of a user in a room to the default user level using a specified comparator.
 * @param room The room in which to check the user's power level.
 * @param user The user whose power level is being checked.
 * @param defaultUserLevel The default user level to compare against.
 * @param comparator The type of comparison to perform.
 * @returns True if the comparison between the user's power level and the default user level is true, false otherwise.
 */
export const compareLevelThanDefaultLevel = (
    room: Room,
    user: ICompletion,
    defaultUserLevel: number,
    comparator: NumberComparison
): boolean => {
    if (user.completionId === undefined) {
        return false;
    }

    const member = room.getMember(user.completionId);

    if (member === null) {
        return false;
    }

    switch (comparator) {
        case NumberComparison.LessThan:
            return member.powerLevel < defaultUserLevel;
        case NumberComparison.LessThanOrEqual:
            return member.powerLevel <= defaultUserLevel;
        case NumberComparison.Equal:
            return member.powerLevel === defaultUserLevel;
        case NumberComparison.GreaterThanOrEqual:
            return member.powerLevel >= defaultUserLevel;
        case NumberComparison.GreaterThan:
            return member.powerLevel > defaultUserLevel;
        case NumberComparison.NotEqual:
            return member.powerLevel !== defaultUserLevel;
        default:
            return false;
    }
};

/**
 * Extracts user IDs from the selected completions.
 * @param completions The selected completions from which to extract user IDs.
 * @returns An array of user IDs extracted from the completions.
 */
export const getUserIdsFromCompletions = (completions: ICompletion[]): string[] => {
    const completionsWithId = completions.filter((completion) => completion.completionId !== undefined);

    // undefined completionId's are filtered out above but TypeScript does not seem to understand.
    return completionsWithId.map((completion) => completion.completionId!);
};
